import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useRouterState } from '@tanstack/react-router';
import { IconMenu2, IconX } from '@tabler/icons-react';
import { useApplyModal } from '../../features/apply/apply-context';
import { Button, Container } from '../../shared/ui';
import { LanguageSwitcher } from '../../shared/ui/language-switcher';
import logo from '../../shared/assets/images/logo.svg';

const pageLinks = [
  { to: '/', key: 'app' },
  { to: '/business', key: 'business' },
] as const;

export function Header() {
  const { t } = useTranslation();
  const { openApplyModal } = useApplyModal();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuToggleRef = useRef<HTMLButtonElement>(null);
  const isAppPage = useRouterState({ select: (state) => state.location.pathname === '/' });

  const closeMenu = () => setMenuOpen(false);
  const dismissMenu = () => {
    closeMenu();
    menuToggleRef.current?.focus();
  };
  const openApplication = () => {
    // Let the dialog restore focus to the toggle, outside the now-inert menu.
    if (menuOpen) dismissMenu();
    openApplyModal();
  };

  return (
    <header
      className="sticky top-4 z-50"
      onKeyDown={(event) => {
        if (event.key === 'Escape' && menuOpen) {
          event.preventDefault();
          event.stopPropagation();
          dismissMenu();
        }
      }}
    >
      {/* Mobile menu scrim (surface/scrim from Figma). Sits above the page but
          below the header card, and dismisses the menu on tap. Kept mounted so
          it can fade in and out; non-interactive while hidden. */}
      <button
        type="button"
        aria-label={t('header.nav.close')}
        aria-hidden={!menuOpen}
        tabIndex={menuOpen ? 0 : -1}
        onClick={dismissMenu}
        className={`fixed inset-0 z-0 bg-[#fafafab8] transition-opacity duration-200 ease-out motion-reduce:transition-none lg:hidden ${
          menuOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
      />
      <Container>
        {/* The bar reserves only its own height (spacer); the card is absolute so
            the expanding dropdown overlays the page instead of shifting it down. */}
        <div className="relative">
          <div className="h-18" aria-hidden />
          <div className="rounded-card absolute inset-x-0 top-0 z-10 bg-white/75 shadow-[0px_0px_15px_0px_rgba(13,12,12,0.05)] backdrop-blur-md">
            <div className="h-18 relative flex items-center justify-between gap-4 px-4">
              <Link to="/" className="flex items-center gap-2" onClick={closeMenu}>
                <img src={logo} alt="money2me" className="h-10 w-auto" />
              </Link>

              {/* Absolutely centered so its position stays fixed regardless of the CTA
                button's width ("Скачать" vs "Оставить заявку") on either side. */}
              <nav
                aria-label={t('header.nav.label')}
                className="text-ink hidden items-center gap-8 text-base lg:absolute lg:left-1/2 lg:top-1/2 lg:flex lg:-translate-x-1/2 lg:-translate-y-1/2"
              >
                {pageLinks.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    activeOptions={{ exact: true }}
                    activeProps={{ className: 'text-accent' }}
                    className="hover:text-accent"
                  >
                    {t(`header.nav.${link.key}`)}
                  </Link>
                ))}
              </nav>

              <div className="flex items-center gap-2">
                <LanguageSwitcher />
                <div className="hidden sm:block">
                  {isAppPage ? (
                    <Button size="sm" href="#download-app">
                      {t('header.cta.download')}
                    </Button>
                  ) : (
                    <Button size="sm" onClick={openApplication}>
                      {t('header.cta.apply')}
                    </Button>
                  )}
                </div>
                <button
                  ref={menuToggleRef}
                  type="button"
                  className="text-ink flex size-10 items-center justify-center rounded-[10px] lg:hidden"
                  aria-expanded={menuOpen}
                  aria-controls="mobile-nav"
                  aria-label={t('header.nav.toggle')}
                  onClick={() => setMenuOpen((open) => !open)}
                >
                  {menuOpen ? <IconX aria-hidden /> : <IconMenu2 aria-hidden />}
                </button>
              </div>
            </div>

            {/* Grid-rows 0fr↔1fr animates to the content's natural height, so the
              menu smoothly expands/collapses without a hard-coded height. */}
            <div
              className={`grid transition-[grid-template-rows,opacity] duration-300 ease-out motion-reduce:transition-none lg:hidden ${
                menuOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
              }`}
            >
              <div className="min-h-0 overflow-hidden">
                <nav
                  id="mobile-nav"
                  aria-label={t('header.nav.label')}
                  inert={!menuOpen}
                  className="border-border text-ink flex flex-col gap-1 border-t px-4 py-3"
                >
                  {pageLinks.map((link) => (
                    <Link
                      key={link.to}
                      to={link.to}
                      activeOptions={{ exact: true }}
                      activeProps={{ className: 'text-accent' }}
                      className="hover:bg-canvas rounded-lg px-2 py-2 text-base"
                      onClick={closeMenu}
                    >
                      {t(`header.nav.${link.key}`)}
                    </Link>
                  ))}
                  <div className="mt-2 sm:hidden">
                    {isAppPage ? (
                      <Button size="sm" href="#download-app" onClick={closeMenu} className="w-full">
                        {t('header.cta.download')}
                      </Button>
                    ) : (
                      <Button size="sm" onClick={openApplication} className="w-full">
                        {t('header.cta.apply')}
                      </Button>
                    )}
                  </div>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </header>
  );
}
